import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { Button } from '@/design-system/primitives';
import { Icon } from '@/lib/Icon';
import { track } from '@/lib/analytics';
import { cn } from '@/lib/cn';
import type { MarketplaceProduct } from '@/types/api';
import { cart, cartItemFromProduct, useCart } from '../stores/cart';
import { cartUi } from '../stores/cartUi';

function formatPrice(cents: number | null | undefined): string | null {
  if (cents == null) return null;
  const value = cents / 100;
  return value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`;
}

function InterestRow({
  product,
  campaignKey,
  messageKey,
  recommendationId,
  onAddToCart,
}: {
  product: MarketplaceProduct;
  campaignKey: string;
  messageKey: string | null;
  recommendationId: string | null;
  onAddToCart?: (product: MarketplaceProduct) => void;
}) {
  const inCart = useCart((s) => s.items.some((item) => item.id === product.id));
  const price = formatPrice(product.price_cents);

  const handleAdd = () => {
    if (inCart) {
      cartUi.open();
      return;
    }
    cart.add({
      ...cartItemFromProduct(product),
      lifecycle_campaign_key: campaignKey,
      lifecycle_message_key: messageKey ?? undefined,
      lifecycle_recommendation_id: recommendationId ?? undefined,
    });
    track('marketplace_product_clicked', {
      source: 'lifecycle_interests',
      campaign_key: campaignKey,
      message_key: messageKey,
      recommendation_id: recommendationId,
      product_id: product.id,
      slug: product.slug,
      price_cents: product.price_cents ?? 0,
    });
    onAddToCart?.(product);
    // Straight into the cart drawer — no second tap on the sticky bar.
    cartUi.open();
  };

  return (
    <div className="flex items-center gap-15 border border-line-subtle bg-page p-12">
      <div className="relative w-70 h-70 shrink-0 bg-sunken overflow-hidden">
        {product.hero_image_url ? (
          <img
            src={product.hero_image_url}
            alt={product.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-4">
        <div className="type-title-sm text-ink truncate">
          {product.title.toUpperCase()}
        </div>
        {(product.treatment_support || product.subtitle) && (
          <div className="type-body-sm text-ink-secondary line-clamp-2">
            {product.treatment_support || product.subtitle}
          </div>
        )}
        {price && <div className="type-label-sm text-ink-muted">{price}</div>}
      </div>
      <Button
        type="button"
        variant={inCart ? 'secondary' : 'filled'}
        className="shrink-0"
        onClick={handleAdd}
      >
        <span>{inCart ? 'In cart' : 'Add'}</span>
        <span
          aria-hidden
          className="inline-flex items-center justify-center w-8"
        >
          <Icon name="Arrow" viewBox="0 0 10 12" />
        </span>
      </Button>
    </div>
  );
}

/**
 * Multi-product lifecycle popup: opened by campaign deep links carrying
 * `?products=slug,slug` — the treatments the patient flagged on the intake
 * "any other treatments you're interested in?" survey. Single-product deep
 * links keep using LifecycleProductModal.
 */
export function LifecycleInterestsModal({
  open,
  onOpenChange,
  products,
  campaignKey,
  messageKey = null,
  recommendationId = null,
  onAddToCart,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: MarketplaceProduct[];
  campaignKey: string;
  messageKey?: string | null;
  recommendationId?: string | null;
  onAddToCart?: (product: MarketplaceProduct) => void;
}) {
  if (!products.length) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-dialog bg-black/20 backdrop-blur-[10px]" />
        <Dialog.Content
          className={cn(
            'fixed z-dialog left-1/2 top-1/2 w-[calc(100vw-30px)] max-w-[64rem]',
            '-translate-x-1/2 -translate-y-1/2 bg-page border border-line-subtle',
            'max-h-[calc(100vh-60px)] overflow-y-auto shadow-modal focus-visible:outline-none',
          )}
        >
          <div className="flex items-center justify-between border-b border-line-subtle bg-sunken">
            <div className="type-label-sm text-ink-secondary px-15 py-15">
              FROM YOUR INTAKE
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close recommendations"
                className="w-45 h-45 inline-flex items-center justify-center border-l border-line-subtle text-ink-secondary hover:text-ink"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </Dialog.Close>
          </div>

          <div className="p-20 md:p-30 flex flex-col gap-20">
            <div className="flex flex-col gap-10">
              <Dialog.Title className="type-title-lg text-ink">
                TREATMENTS YOU WERE INTERESTED IN
              </Dialog.Title>
              <Dialog.Description className="type-body-md text-ink-secondary">
                When you first joined, you told us you were also interested in
                trying these. They're available to add to your stack.
              </Dialog.Description>
            </div>

            <div className="flex flex-col gap-10">
              {products.map((product) => (
                <InterestRow
                  key={product.id}
                  product={product}
                  campaignKey={campaignKey}
                  messageKey={messageKey}
                  recommendationId={recommendationId}
                  onAddToCart={onAddToCart}
                />
              ))}
            </div>

            <p className="type-body-sm text-ink-muted">
              Your clinician reviews anything that needs clinical approval
              before it ships.
            </p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
